import React, { useState, useEffect } from 'react';
import axios from '../config/axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';

export default function AdminTickets() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssigned, setFilterAssigned] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);

  // Form states
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: '',
    assignedTo: ''
  });
  const [comment, setComment] = useState('');

  useEffect(() => {
    fetchTickets();
    fetchStats();
    fetchAdminUsers();
  }, [filterStatus, filterPriority, filterAssigned]);

  const fetchTickets = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterPriority) params.append('priority', filterPriority);
      if (filterAssigned) params.append('assignedTo', filterAssigned);

      const response = await axios.get(`/cms/admin-tickets?${params.toString()}`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setTickets([]); // Set to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/cms/admin-tickets/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const response = await axios.get('/cms/admins');
      setAdminUsers(response.data.admins || []);
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const response = await axios.get(`/cms/admin-tickets/${ticketId}`);
      setSelectedTicket(response.data.ticket);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      alert('Error loading ticket details');
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/cms/admin-tickets', newTicket);
      setShowCreateModal(false);
      setNewTicket({
        title: '',
        description: '',
        priority: 'medium',
        category: '',
        assignedTo: ''
      });
      fetchTickets();
      fetchStats();
      alert('Ticket created successfully!');
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Error creating ticket');
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      await axios.put(`/cms/admin-tickets/${ticketId}`, { status: newStatus });
      fetchTickets();
      fetchStats();
      if (selectedTicket && selectedTicket.id === ticketId) {
        fetchTicketDetails(ticketId);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating ticket status');
    }
  };

  const handleAssignTicket = async (ticketId, adminId) => {
    try {
      await axios.put(`/cms/admin-tickets/${ticketId}`, { assignedTo: adminId || null });
      fetchTickets();
      if (selectedTicket && selectedTicket.id === ticketId) {
        fetchTicketDetails(ticketId);
      }
    } catch (error) {
      console.error('Error assigning ticket:', error);
      alert('Error assigning ticket');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    try {
      await axios.post(`/cms/admin-tickets/${selectedTicket.id}/comments`, {
        comment: comment.trim()
      });
      setComment('');
      fetchTicketDetails(selectedTicket.id);
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error adding comment');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-green-100 text-green-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-secondary-100 text-secondary-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.open;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading tickets...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600">{t('cms.support.adminTickets')}</h1>
          <p className="text-gray-600">{t('cms.support.title')}</p>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Total Tickets</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Open</div>
            <div className="text-2xl font-bold text-green-600">{stats.open}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">In Progress</div>
            <div className="text-2xl font-bold text-yellow-600">{stats.in_progress}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Urgent</div>
            <div className="text-2xl font-bold text-red-600">{stats.urgent}</div>
          </div>
        </div>
      )}

      {/* Filters and Actions */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2"
            >
              <option value="">{t('cms.common.status')}</option>
              <option value="open">{t('cms.support.open')}</option>
              <option value="in_progress">{t('cms.support.inProgress')}</option>
              <option value="resolved">{t('cms.support.resolved')}</option>
              <option value="closed">{t('cms.support.closed')}</option>
            </select>

            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>

            <select
              value={filterAssigned}
              onChange={(e) => setFilterAssigned(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Assignments</option>
              <option value="me">Assigned to Me</option>
              <option value="unassigned">Unassigned</option>
              {user?.role === 'master_admin' && adminUsers.map(admin => (
                <option key={admin.id} value={admin.id}>{admin.full_name || admin.username}</option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            + {t('cms.support.createTicket')}
          </button>
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cms.common.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {!tickets || tickets.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    {t('cms.support.noTickets')}
                  </td>
                </tr>
              ) : (
                tickets.map(ticket => (
                  <tr key={ticket.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {ticket.ticket_number}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="max-w-xs truncate">{ticket.title}</div>
                      {ticket.comment_count > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          💬 {ticket.comment_count} {ticket.comment_count === 1 ? 'comment' : 'comments'}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(ticket.status)}`}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {ticket.creator_name || ticket.creator_username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {ticket.assignee_name || ticket.assignee_username || (
                        <span className="text-gray-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => fetchTicketDetails(ticket.id)}
                        className="text-primary-600 hover:text-primary-800 font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => { setShowCreateModal(false); setNewTicket({ title: '', description: '', priority: 'medium', category: '', assigned_to: '' }); }}
          onKeyDown={(e) => { if (e.key === 'Escape') { setShowCreateModal(false); setNewTicket({ title: '', description: '', priority: 'medium', category: '', assigned_to: '' }); } }}
          tabIndex={0}
          ref={(el) => el && el.focus()}
        >
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">{t('cms.support.createTicket')}</h2>
              <form onSubmit={handleCreateTicket}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      value={newTicket.title}
                      onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                    <textarea
                      value={newTicket.description}
                      onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 h-32"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      <select
                        value={newTicket.priority}
                        onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <input
                        type="text"
                        value={newTicket.category}
                        onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        placeholder="e.g., Technical, Content, etc."
                      />
                    </div>
                  </div>

                  {user?.role === 'master_admin' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                      <select
                        value={newTicket.assigned_to}
                        onChange={(e) => setNewTicket({ ...newTicket, assigned_to: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="">Unassigned</option>
                        {adminUsers.map(admin => (
                          <option key={admin.id} value={admin.id}>
                            {admin.full_name || admin.username}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="btn btn-primary flex-1"
                  >
                    {t('cms.support.createTicket')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setNewTicket({
                        title: '',
                        description: '',
                        priority: 'medium',
                        category: '',
                        assigned_to: ''
                      });
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Ticket Modal */}
      {showModal && selectedTicket && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => { setShowModal(false); setSelectedTicket(null); }}
          onKeyDown={(e) => { if (e.key === 'Escape') { setShowModal(false); setSelectedTicket(null); } }}
          tabIndex={0}
          ref={(el) => el && el.focus()}
        >
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold">{selectedTicket.ticket_number}</h2>
                  <h3 className="text-lg text-gray-700 mt-1">{selectedTicket.title}</h3>
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedTicket(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {/* Ticket Metadata */}
              <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 rounded">
                <div>
                  <div className="text-sm text-gray-600">{t('cms.common.status')}</div>
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => handleUpdateStatus(selectedTicket.id, e.target.value)}
                    disabled={user?.role !== 'master_admin' && selectedTicket.created_by !== user?.id}
                    className="mt-1 border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="open">{t('cms.support.open')}</option>
                    <option value="in_progress">{t('cms.support.inProgress')}</option>
                    <option value="resolved">{t('cms.support.resolved')}</option>
                    <option value="closed">{t('cms.support.closed')}</option>
                  </select>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Priority</div>
                  <span className={`inline-block mt-1 px-2 py-1 text-xs rounded-full ${getPriorityColor(selectedTicket.priority)}`}>
                    {selectedTicket.priority}
                  </span>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Created By</div>
                  <div className="mt-1 text-sm">{selectedTicket.creator_name || selectedTicket.creator_username}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Assigned To</div>
                  {user?.role === 'master_admin' ? (
                    <select
                      value={selectedTicket.assigned_to || ''}
                      onChange={(e) => handleAssignTicket(selectedTicket.id, e.target.value)}
                      className="mt-1 border border-gray-300 rounded px-2 py-1 text-sm w-full"
                    >
                      <option value="">Unassigned</option>
                      {adminUsers.map(admin => (
                        <option key={admin.id} value={admin.id}>
                          {admin.full_name || admin.username}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="mt-1 text-sm">
                      {selectedTicket.assignee_name || selectedTicket.assignee_username || (
                        <span className="text-gray-400">Unassigned</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="mb-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Description</div>
                <div className="p-4 bg-gray-50 rounded whitespace-pre-wrap">
                  {selectedTicket.description}
                </div>
              </div>

              {/* Comments */}
              <div className="mb-4">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Comments ({selectedTicket.comments?.length || 0})
                </div>
                <div className="space-y-3 mb-3 max-h-64 overflow-y-auto">
                  {selectedTicket.comments && selectedTicket.comments.length > 0 ? (
                    selectedTicket.comments.map(comment => (
                      <div key={comment.id} className="p-3 bg-gray-50 rounded">
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-medium text-sm">
                            {comment.admin_name || comment.admin_username}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(comment.created_at).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-sm whitespace-pre-wrap">{comment.comment}</div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-4">No comments yet</div>
                  )}
                </div>

                {/* Add Comment Form */}
                <form onSubmit={handleAddComment}>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="w-full border border-gray-300 rounded px-3 py-2 mb-2"
                    rows="3"
                  />
                  <button
                    type="submit"
                    disabled={!comment.trim()}
                    className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-4 py-2 rounded hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Comment
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
